import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { marketplaceComponentTemplates, widgetTemplates } from '../data/widgets';
import { db } from '../firebase';

function requireDb() {
  if (!db) {
    throw new Error('Firestore is not configured. Add REACT_APP_FIREBASE_* values to .env.local.');
  }
}

function timestampValue(value) {
  if (!value) {
    return 0;
  }
  if (typeof value.toMillis === 'function') {
    return value.toMillis();
  }
  return Number(new Date(value)) || 0;
}

function instancesRef(uid) {
  return collection(db, 'userWidgets', uid, 'instances');
}

function instanceRef(uid, instanceId) {
  return doc(db, 'userWidgets', uid, 'instances', instanceId);
}

function sortInstances(items) {
  return [...items].sort((a, b) => {
    if ((a.position || 0) !== (b.position || 0)) {
      return (a.position || 0) - (b.position || 0);
    }
    return timestampValue(b.updatedAt || b.createdAt) - timestampValue(a.updatedAt || a.createdAt);
  });
}

export async function listUserWidgets(uid) {
  requireDb();
  const snapshot = await getDocs(instancesRef(uid));
  return sortInstances(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function listPublicUserWidgets(uid) {
  requireDb();
  const snapshot = await getDocs(query(instancesRef(uid), where('visibility', '==', 'public')));
  return sortInstances(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

async function isFriendWithOwner(ownerId, viewerUid) {
  if (!viewerUid || viewerUid === ownerId) {
    return false;
  }

  requireDb();
  const snapshot = await getDoc(doc(db, 'profileFriends', ownerId, 'friends', viewerUid));
  return snapshot.exists();
}

export async function listVisibleUserWidgets(uid, viewerUid = null) {
  requireDb();
  if (viewerUid === uid) {
    return listUserWidgets(uid);
  }

  const visibilityQuery = await isFriendWithOwner(uid, viewerUid)
    ? query(instancesRef(uid), where('visibility', 'in', ['public', 'friends']))
    : query(instancesRef(uid), where('visibility', '==', 'public'));
  const snapshot = await getDocs(visibilityQuery);
  return sortInstances(snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
}

export async function installWidgetTemplate(uid, template) {
  requireDb();
  const existing = await listUserWidgets(uid);
  const now = serverTimestamp();
  const ref = await addDoc(instancesRef(uid), {
    ownerId: uid,
    templateId: template.id,
    name: template.name,
    description: template.description,
    fields: template.fields,
    settings: template.defaultSettings || {},
    visibility: template.visibility || 'private',
    position: existing.length,
    source: 'template',
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}

export async function updateUserWidget(uid, instanceId, widget) {
  requireDb();
  await updateDoc(instanceRef(uid, instanceId), {
    name: widget.name.trim(),
    description: widget.description.trim(),
    settings: widget.settings || {},
    visibility: widget.visibility,
    position: Number(widget.position) || 0,
    nsfw: Boolean(widget.nsfw),
    adult: Boolean(widget.adult),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteUserWidget(uid, instanceId) {
  requireDb();
  await deleteDoc(instanceRef(uid, instanceId));
}

export function marketplaceTemplates() {
  return widgetTemplates;
}

export async function listPublishedComponents() {
  if (!db) {
    return [];
  }

  const snapshot = await getDocs(query(collection(db, 'publishedWidgets'), where('visibility', '==', 'public')));
  return snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data(), source: 'community' }));
}

export async function listMarketplaceComponents(options = {}) {
  const published = await listPublishedComponents();
  return marketplaceComponentTemplates([
    ...widgetTemplates,
    ...published,
  ], options);
}

export async function publishUserWidget(uid, widget) {
  requireDb();
  const now = serverTimestamp();
  const ref = await addDoc(collection(db, 'publishedWidgets'), {
    ownerId: uid,
    templateId: widget.templateId || widget.id,
    name: widget.name.trim(),
    description: widget.description.trim(),
    fields: widget.fields || Object.keys(widget.settings || {}),
    settings: widget.settings || {},
    visibility: 'public',
    position: 0,
    source: 'community',
    nsfw: Boolean(widget.nsfw),
    adult: Boolean(widget.adult),
    createdAt: now,
    updatedAt: now,
  });
  return ref.id;
}
